import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateBookingQueries } from "@/lib/queryClient";
import { OccurrenceAmenityService } from "@/services/OccurrenceAmenityService";

/** R27 — per-date amenities for a repeating booking (elevated only; callers gate `enabled`). */
export const occurrenceAmenitiesKey = (bookingId: string) => ["occurrence-amenities", bookingId] as const;

export function useOccurrenceAmenities(bookingId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: occurrenceAmenitiesKey(bookingId ?? ""),
    queryFn: () => new OccurrenceAmenityService().getOccurrenceAmenities(bookingId as string),
    enabled: !!bookingId && enabled,
  });
}

function useInvalidateAfter(bookingId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: occurrenceAmenitiesKey(bookingId) });
    await invalidateBookingQueries();
  };
}

export function useSetOccurrenceAmenities(bookingId: string) {
  const invalidate = useInvalidateAfter(bookingId);
  return useMutation({
    mutationFn: ({ dates, amenityIds }: { dates: string[]; amenityIds: string[] }) =>
      new OccurrenceAmenityService().setOccurrenceAmenities(bookingId, dates, amenityIds),
    onSuccess: invalidate,
  });
}

export function useResetOccurrenceAmenities(bookingId: string) {
  const invalidate = useInvalidateAfter(bookingId);
  return useMutation({
    mutationFn: ({ dates }: { dates: string[] }) => new OccurrenceAmenityService().resetOccurrenceAmenities(bookingId, dates),
    onSuccess: invalidate,
  });
}
